import { useState, useCallback } from "react";
import { ApiResponse, Session } from "../../services/Dtos";
import { ApiService } from "../../services/ApiService";
import useAxios from "../useAxios";
import { useSchoolsContext } from "../../Contexts/schoolContext";
import { useApiResponseHandler } from "../useApiResponseHandler";

export const useSessionService = () => {
  const { setSessions, setSchool } = useSchoolsContext();
  const axiosClient = useAxios();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const { handleApiError, handleApiSuccess, clearErrors } =
    useApiResponseHandler({
      setErrors,
    });

  const createSession = useCallback(
    async (
      session: Session,
      onSuccess?: (session: Session) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<Session>(
          "/sessions",
          axiosClient
        ).create(session);
        if (result.succeeded) {
          const createdSession = result.data;
          setSessions((prev: Session[]) =>
            [...prev, createdSession].sort((a, b) => b.order - a.order)
          );
          handleApiSuccess("Session created successfully");
          onSuccess?.(createdSession);
          return { result };
        } else {
          handleApiError(result, "Failed to create session");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to create session");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to create session",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setSessions]
  );

  const updateSession = useCallback(
    async (
      id: string,
      session: Session,
      onSuccess?: (session: Session) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<Session>(
          "/sessions",
          axiosClient
        ).update(id, session);
        if (result.succeeded) {
          setSessions((prev: Session[]) =>
            prev.map((s) => (s.id === id ? session : s))
          );
          handleApiSuccess("Session updated successfully");
          onSuccess?.(session);
          return { result };
        } else {
          handleApiError(result, "Failed to update session");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to update session");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update session",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setSessions]
  );

  const deleteSession = useCallback(
    async (
      id: string,
      onSuccess?: (id: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<Session>(
          "/sessions",
          axiosClient
        ).delete(id);
        if (result.succeeded) {
          setSessions((prev: Session[]) => prev.filter((s) => s.id !== id));
          setSchool((prev: any) => {
            if (!prev) return prev;
            if (prev.currentSessionId === id) {
              return { ...prev, currentSessionId: "" };
            }
            return prev;
          });
          handleApiSuccess("Session deleted successfully");
          onSuccess?.(id);
          return { result };
        } else {
          handleApiError(result, "Failed to delete session");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to delete session");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to delete session",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
        setOpenDeleteDialog(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess]
  );

  return {
    createSession,
    updateSession,
    deleteSession,
    isLoading,
    errors,
    setErrors,
    clearErrors,
    openDeleteDialog,
    setOpenDeleteDialog,
  };
};
